import React, { useMemo, useState } from 'react';
import { X, Zap } from 'lucide-react';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@cotera/client/v0/components/ui/popover';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@cotera/client/v0/components/ui/command';
import { cn } from '@cotera/client/v0/lib/utils';
import {
  displayLabelForRef,
  type UpstreamSourceGroup,
} from './variable-picker-data';
import {
  singleVariableSection,
  sourceSections,
  VariablePickerRow,
  VARIABLE_PICKER_EMPTY_TEXT,
  VARIABLE_PICKER_WIDTH,
  type VariablePickerSection,
} from './variable-picker';

/**
 * Popover that lists pickable fields for a single-ref binding.
 *
 * The popover surface of the shared picker: rows, sections, search and the
 * keyboard legend are the same ones the `/` menu renders, so the two read as
 * one control shown two ways rather than two controls that happen to list the
 * same fields.
 *
 * Sections are the caller's, so this also serves a surface with no upstream
 * graph — the dataset trigger's condition field, whose sections are that
 * dataset's own columns.
 */
export const VariableSectionPicker: React.FC<{
  sections: readonly VariablePickerSection[];
  onPick: (ref: string) => void;
  placeholder?: string;
  /**
   * Shown when the picker has no sections at all — nothing upstream to offer, as
   * opposed to a search that matched nothing. Defaults to the same text as the
   * search-empty case, so callers that don't distinguish the two see no change.
   */
  noFieldsText?: string;
  children: React.ReactNode;
}> = ({
  sections,
  onPick,
  placeholder = 'Search fields…',
  noFieldsText = VARIABLE_PICKER_EMPTY_TEXT,
  children,
}) => {
  const [open, setOpen] = useState(false);
  const pick = (ref: string): void => {
    onPick(ref);
    setOpen(false);
  };
  // A picker with sections can only be empty because the query filtered them
  // out; one with no sections has nothing to offer yet. Two different things to
  // say, and "No matching fields" is a lie for the second.
  const emptyText =
    sections.length === 0 ? noFieldsText : VARIABLE_PICKER_EMPTY_TEXT;
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>{children}</PopoverTrigger>
      <PopoverContent
        // The trigger is a bolt at the right edge of its field, so `start`
        // (grow rightward from the button's left) ran the panel off the card's
        // right edge and into the canvas. `end` right-aligns it to the button
        // and grows it leftward, keeping it inside the card; `collisionPadding`
        // holds a margin from the edge rather than letting it butt right up.
        align="end"
        collisionPadding={12}
        className={cn('min-w-[280px] p-0', VARIABLE_PICKER_WIDTH)}
      >
        <Command>
          <CommandInput placeholder={placeholder} />
          <CommandList>
            <CommandEmpty>{emptyText}</CommandEmpty>
            {sections.map((section) => (
              <CommandGroup
                key={section.heading}
                heading={section.heading}
                // Freeze the source heading to the top of the scroll list so it
                // stays visible while scrolling its (potentially long) run of
                // fields. `overflow-visible` undoes the group's default clip,
                // which would otherwise pin `sticky` to the group box rather
                // than the list and let the heading scroll away with it; `pt-0`
                // drops the group's top padding, which would otherwise be a
                // strip above the `top-0` heading that scrolling rows show
                // through. `-mx-1` cancels the group's horizontal padding so the
                // filled band runs edge to edge of the popover (its `px-3` keeps
                // the label aligned with the rows below). A filled band and
                // stronger text keep the frozen header legible against the rows
                // beneath instead of blending into them.
                className="overflow-visible pt-0 [&_[data-slot=command-group-heading]]:bg-muted [&_[data-slot=command-group-heading]]:text-foreground/80 [&_[data-slot=command-group-heading]]:sticky [&_[data-slot=command-group-heading]]:top-0 [&_[data-slot=command-group-heading]]:z-10 [&_[data-slot=command-group-heading]]:-mx-1 [&_[data-slot=command-group-heading]]:border-b [&_[data-slot=command-group-heading]]:border-foreground/10 [&_[data-slot=command-group-heading]]:px-3 [&_[data-slot=command-group-heading]]:font-semibold [&_[data-slot=command-group-heading]]:uppercase [&_[data-slot=command-group-heading]]:tracking-wide"
              >
                {section.fields.map((field) => (
                  <CommandItem
                    key={`${field.sourceName}:${field.ref}`}
                    value={field.searchText}
                    className="justify-between gap-3"
                    onSelect={() => pick(field.ref)}
                  >
                    <VariablePickerRow field={field} />
                  </CommandItem>
                ))}
              </CommandGroup>
            ))}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
};

/**
 * The same picker over a node's upstream sources, split into "Previous step"
 * and "Earlier outputs". Kept per-source (no union) so a field is always
 * attributable to the node that produced it.
 */
export const UpstreamRefPicker: React.FC<{
  sources: readonly UpstreamSourceGroup[];
  onPick: (ref: string) => void;
  /**
   * One heading for every field, instead of the "Previous step" / "Earlier
   * outputs" split. For a picker whose fields belong to a value rather than to
   * a step the reader could go and look at.
   */
  heading?: string;
  /** What the popover says when there are no fields to offer yet. */
  noFieldsText?: string;
  children: React.ReactNode;
}> = ({ sources, onPick, heading, noFieldsText, children }) => {
  const sections = useMemo(
    () =>
      heading === undefined
        ? sourceSections(sources)
        : singleVariableSection(heading, sources),
    [sources, heading]
  );
  return (
    <VariableSectionPicker
      sections={sections}
      onPick={onPick}
      placeholder="Search output fields…"
      noFieldsText={noFieldsText}
    >
      {children}
    </VariableSectionPicker>
  );
};

/** Chip showing a selected upstream ref, with clear. */
export const UpstreamRefChip: React.FC<{
  refValue: string;
  sourceLabels: ReadonlyMap<string, string>;
  onRemove: () => void;
  removeTitle?: string;
}> = ({
  refValue,
  sourceLabels,
  onRemove,
  removeTitle = 'Clear selection',
}) => (
  <span className="bg-primary/10 text-primary inline-flex h-8 items-center gap-1.5 rounded-md px-2 font-mono text-xs">
    {displayLabelForRef(refValue, sourceLabels)}
    <button
      type="button"
      onClick={onRemove}
      className="hover:bg-primary/20 rounded"
      title={removeTitle}
    >
      <X className="size-3" />
    </button>
  </span>
);

/** Zap button that opens {@link UpstreamRefPicker}. */
export const UpstreamRefPickerButton: React.FC<{
  sources: readonly UpstreamSourceGroup[];
  onPick: (ref: string) => void;
  title?: string;
  /** Passed through to {@link UpstreamRefPicker}. */
  heading?: string;
  /**
   * Keep the button visible even with nothing to offer, opening to a
   * "nothing yet" empty state instead of vanishing.
   *
   * Off by default: for a button that only *changes* an existing selection
   * (for-each, bucket) there is nothing to do when the picker is empty, so it
   * should disappear. A field whose *only* upstream affordance is this button
   * — the group-by pointers — turns it on, so the field never collapses to a
   * bare text box that looks like it was never wired for picking at all.
   */
  showWhenEmpty?: boolean;
  /** What the popover says when there are no fields to offer yet. */
  noFieldsText?: string;
  /**
   * Where the button sits relative to the field it fills in.
   *
   * `inline` puts it inside the box, at the icon and inset the token input
   * already uses for the same job — which is what a column of fields wants,
   * since a button rendered beside a box shortens that one field and leaves
   * the panel with two right edges and two places to look for one affordance.
   * It needs a `relative` parent and room made for it (see
   * {@link INLINE_PICKER_INSET}), so `beside` stays the default: it is also
   * the only option for a control that cannot host it — a select's top-right
   * corner already belongs to its chevron — or for a button that is not
   * trailing a box at all.
   */
  variant?: 'inline' | 'beside';
}> = ({
  sources,
  onPick,
  title = 'Use data from a previous step',
  heading,
  showWhenEmpty = false,
  noFieldsText,
  variant = 'beside',
}) => {
  if (sources.length === 0 && !showWhenEmpty) {
    return null;
  }
  return (
    <UpstreamRefPicker
      sources={sources}
      onPick={onPick}
      heading={heading}
      noFieldsText={noFieldsText}
    >
      <button
        type="button"
        title={title}
        className={cn(
          'text-muted-foreground hover:text-foreground hover:bg-muted/60 flex shrink-0 items-center justify-center',
          variant === 'inline'
            ? // Positioned against the box, which the caller makes `relative`
              // and pads so the value cannot run underneath.
              'absolute right-1 top-1 size-6 rounded'
            : 'size-8 rounded-md border'
        )}
      >
        <Zap className="size-4" />
      </button>
    </UpstreamRefPicker>
  );
};

/** Right padding a box needs to clear an inline {@link UpstreamRefPickerButton}. */
export const INLINE_PICKER_INSET = 'pr-9';
